import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
import { buttonVariants } from "../button";
import { ToastPauseProvider, useToastPause, useToastTimer } from "./toast-timing";
import { useToastCloseRules } from "./toast-close";

/**
 * Toast - a short message that arrives in a corner, says one thing, and leaves.
 *
 * Built on Radix Toast for the parts that are easy to get wrong by hand: the live
 * region that reads the message out, swipe to close, and the F8 hotkey that moves
 * focus into the stack (where Escape then closes the toast).
 *
 * What the kit adds on top:
 * - The countdown. A timed toast draws a thin bar along its bottom edge that runs
 *   down to nothing over `duration`, so the reader can see how long the message
 *   stays. The timer is the kit's own (`toast-timing.ts`, with the reasons Radix's
 *   could not stay): it holds while the pointer or focus is on the stack or the
 *   window is in the background, and the bar holds with it, from the same state.
 * - The close rules (`toast-close.ts`): an Escape pressed elsewhere on the page
 *   does not throw the message away, and `dismissible={false}` makes a toast that
 *   only its owner can close.
 *
 * `duration={Infinity}` means no timer and no bar. Use that for anything that is
 * still true after it is said (a failure) or that asks a question: a question that
 * closes on its own is an answer nobody gave.
 *
 * Under reduced motion the bar is not drawn. The toast still closes on time and
 * still pauses under the pointer; only the moving part goes. (The admin apps clamp
 * every animation to 0.01ms there, which would otherwise show an empty bar for the
 * whole life of the toast.)
 */

/** How long a timed toast stays when the caller does not say. */
export const TOAST_DURATION = 6000;

/** Radix's provider plus the kit's pause state, which every toast and the viewport share. */
export function ToastProvider({ children, ...props }: React.ComponentPropsWithoutRef<typeof ToastPrimitive.Provider>) {
  return (
    <ToastPrimitive.Provider {...props}>
      <ToastPauseProvider>{children}</ToastPauseProvider>
    </ToastPrimitive.Provider>
  );
}

/**
 * The stack. Bottom right by default, full width on a phone. Pass `className` to
 * move it or to lift it above a surface with its own stacking (the on-page editor).
 *
 * z-[150]: above a Dialog (70) and a Popover (120), because a toast reports on what
 * was just done, often from inside one of them; below a Select list (210) and a
 * Tooltip (230), which are open under the pointer right now.
 */
export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => {
  const { bindViewport } = useToastPause();
  const setRefs = React.useCallback(
    (node: HTMLOListElement | null) => {
      bindViewport(node);
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref, bindViewport],
  );
  return (
    <ToastPrimitive.Viewport
      ref={setRefs}
      className={cn(
        "fixed bottom-0 right-0 z-[150] m-0 flex max-h-dvh w-full list-none flex-col gap-2 p-4 outline-none sm:max-w-[420px]",
        className,
      )}
      {...props}
    />
  );
});
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

const toastVariants = cva(
  [
    "group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-xl border bg-card p-4 pr-10 text-card-foreground shadow-floating",
    "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-right-full",
    "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform",
    "data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:animate-out",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "border-border",
        success: "border-border",
        destructive: "border-destructive/50",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

/** The countdown takes the status colour, so a failure and a receipt do not look alike. */
const BAR_TONE: Record<NonNullable<VariantProps<typeof toastVariants>["variant"]>, string> = {
  default: "bg-primary",
  success: "bg-success",
  destructive: "bg-destructive",
};

export interface ToastProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {
  /** Accessible name of the corner close button. The kit has no translations of
   *  its own, so the default is English; see DialogContent `closeLabel`. */
  closeLabel?: string;
  /** Drop the corner close button. The toast then needs another way out. */
  hideClose?: boolean;
  /**
   * False: nothing but the owner closes it - no X, no swipe, no Escape, no timer.
   * For a toast that asks a question whose answer must be given; pair it with
   * `duration={Infinity}` and set `open` to false once it is answered.
   */
  dismissible?: boolean;
}

export const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitive.Root>, ToastProps>(
  (
    {
      className,
      variant,
      duration = TOAST_DURATION,
      closeLabel = "Close",
      hideClose,
      dismissible = true,
      open: openProp,
      defaultOpen,
      onOpenChange,
      onEscapeKeyDown,
      onSwipeStart,
      onSwipeMove,
      onSwipeEnd,
      children,
      ...props
    },
    ref,
  ) => {
    const { paused, register } = useToastPause();
    const { open, nodeRef, markEscape, requestOpenChange } = useToastCloseRules(
      openProp,
      defaultOpen,
      onOpenChange,
      dismissible,
    );
    React.useEffect(() => (open ? register() : undefined), [open, register]);
    useToastTimer(open, duration, paused, () => requestOpenChange(false));
    const setRefs = React.useCallback(
      (node: HTMLLIElement | null) => {
        nodeRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref, nodeRef],
    );
    // A toast that cannot be dismissed must not slide away under a finger either:
    // preventing the swipe events stops Radix from moving it and from closing it.
    const holdSwipe = (event: { preventDefault(): void }) => {
      if (!dismissible) event.preventDefault();
    };
    const timed = Number.isFinite(duration);
    return (
      <ToastPrimitive.Root
        ref={setRefs}
        open={open}
        onOpenChange={requestOpenChange}
        onEscapeKeyDown={(event) => {
          onEscapeKeyDown?.(event);
          markEscape();
        }}
        onSwipeStart={(event) => {
          onSwipeStart?.(event);
          holdSwipe(event);
        }}
        onSwipeMove={(event) => {
          onSwipeMove?.(event);
          holdSwipe(event);
        }}
        onSwipeEnd={(event) => {
          onSwipeEnd?.(event);
          holdSwipe(event);
        }}
        // Radix never closes a toast on time here; the kit's timer does (see
        // `toast-timing.ts` for why Radix's own cannot be trusted with it).
        duration={Infinity}
        className={cn(toastVariants({ variant }), className)}
        {...props}
      >
        {children}
        {!hideClose && dismissible && (
          <ToastPrimitive.Close
            className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-4" />
            <span className="sr-only">{closeLabel}</span>
          </ToastPrimitive.Close>
        )}
        {timed && dismissible && (
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-x-0 bottom-0 h-1 origin-left motion-reduce:hidden",
              BAR_TONE[variant ?? "default"],
            )}
            style={{
              animation: `wa-toast-countdown ${duration}ms linear forwards`,
              animationPlayState: paused ? "paused" : "running",
            }}
          />
        )}
      </ToastPrimitive.Root>
    );
  },
);
Toast.displayName = ToastPrimitive.Root.displayName;

/** Text column beside an optional icon: title, description and action stack here. */
export const ToastContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid min-w-0 flex-1 gap-1", className)} {...props} />
);

export const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title ref={ref} className={cn("text-sm font-semibold leading-snug", className)} {...props} />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

/** Foreground, not muted: in most toasts this line IS the message. */
export const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description ref={ref} className={cn("text-sm leading-snug text-foreground", className)} {...props} />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

/**
 * The one thing a toast lets you do. Radix requires `altText`: the screen reader
 * hears the message long before it could reach the button, so the alt text says how
 * to do the same thing another way ("Open the template from the Blog menu").
 * `asChild` turns it into a link.
 */
export const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    className={cn(buttonVariants({ variant: "outline", size: "sm" }), "mt-1 justify-self-start", className)}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitive.Action.displayName;

export const ToastClose = ToastPrimitive.Close;
